import React, { useState, Suspense, lazy, useEffect } from "react";
import { colors } from "../../assets/components/ai/constants";
import { Loader2 } from "lucide-react";
const Checkboxtype = lazy(() => import("../QuestionTypes/Checkboxtype"));
const ImageMCQ = lazy(() => import("../QuestionTypes/ImageMCQ"));
const HorizontalMCQ = lazy(() => import("../QuestionTypes/HorizontalMCQ"));
const Shapetype = lazy(() => import("../QuestionTypes/Shapetype"));
const Entertext = lazy(() => import("../QuestionTypes/Entertext"));
const Dots = lazy(() => import("../QuestionTypes/Dots"));
const VerticalImageMcq = lazy(() =>
  import("../QuestionTypes/VerticalImageMCQ")
);
const EvenOddtype = lazy(() => import("../QuestionTypes/EvenOddtype"));
const Orderdrop = lazy(() => import("../QuestionTypes/Orderdrop/Orderdrop"));
const Slidertype = lazy(() => import("../QuestionTypes/Slidertype"));
const TextAndSequence = lazy(() => import("../QuestionTypes/TextAndSequence"));
const Reflection = lazy(() => import("../QuestionTypes/Reflection"));
const ReplaceValues = lazy(() => import("../QuestionTypes/ReplaceValues"));
const MatchType = lazy(() => import("../QuestionTypes/MatchType"));
const Sortbox = lazy(() => import("../QuestionTypes/Sortbox"));
const DetailedMCQ = lazy(() => import("../QuestionTypes/DetailedMCQ"));
const Multipletext = lazy(() => import("../QuestionTypes/Multipletext"));
const Coordinateplane = lazy(() =>
  import("../QuestionTypes/Coordinateplane/Coordinateplane")
);
const Pointsplot = lazy(() => import("../QuestionTypes/Pointsplot"));
const BarGraph = lazy(() => import("../QuestionTypes/BarGraph"));
const ColorCircle = lazy(() => import("../QuestionTypes/ColorCircle"));
const ColorSquare = lazy(() => import("../QuestionTypes/ColorSquare"));
const componentMap = {
  Shapetype,
  Checkboxtype,
  ImageMCQ,
  EvenOddtype,
  TextAndSequence,
  Slidertype,
  Reflection,
  MatchType,
  HorizontalMCQ,
  Entertext,
  Dots,
  Orderdrop,
  VerticalImageMcq,
  ReplaceValues,
  Sortbox,
  DetailedMCQ,
  Multipletext,
  Coordinateplane,
  BarGraph,
  Pointsplot,
  ColorCircle,
  ColorSquare,
};

const resolveQuestionType = (fields = {}) => {
  const questionTypeOriginal = String(fields?.questionTypeOriginal || "")
    .trim()
    .toLowerCase();

  if (questionTypeOriginal === "colorcircle") {
    return "ColorCircle";
  }
  if (
    questionTypeOriginal === "colorsquare" ||
    questionTypeOriginal === "colorrectangle"
  ) {
    return "ColorSquare";
  }

  return fields?.questionType;
};

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <div>This type is not handled yet.</div>;
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[320px] w-full">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      }
    >
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { onError: () => setHasError(true) })
      )}
    </Suspense>
  );
};

const HandleAIQuestions = ({ questions, yearId, onQuestionsComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [attempts, setAttempts] = useState({});
  const [timeTaken, setTimeTaken] = useState({});
  const [startTime, setStartTime] = useState(Date.now());

  useEffect(() => {
    setStartTime(Date.now());
  }, [currentQuestionIndex]);

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAttempts({});
    setTimeTaken({});
    setStartTime(Date.now());
  }, [questions, yearId]);

  const handleAnswer = (_isCorrect = true, attemptCount = 1, questionTime = 0) => {
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    const questionId = currentQuestion.pk.toString();
    const normalizedAttemptCount = Number(attemptCount);
    const safeAttemptCount =
      Number.isFinite(normalizedAttemptCount) && normalizedAttemptCount > 0
        ? normalizedAttemptCount
        : 1;
    const normalizedQuestionTime = Number(questionTime);
    const elapsedQuestionTime = (Date.now() - startTime) / 1000;
    const safeQuestionTime =
      Number.isFinite(normalizedQuestionTime) && normalizedQuestionTime >= 0
        ? normalizedQuestionTime
        : elapsedQuestionTime;

    const newAttempts = {
      ...attempts,
      [questionId]: safeAttemptCount,
    };

    const newTimeTaken = {
      ...timeTaken,
      [questionId]: parseFloat(safeQuestionTime.toFixed(3)),
    };

    setAttempts(newAttempts);
    setTimeTaken(newTimeTaken);

    if (currentQuestionIndex === questions.length - 1) {
      const lessonIds = {};
      const yearIds = {};

      questions.forEach((q) => {
        const qId = q.pk.toString();
        lessonIds[qId] = q.fields.lession.toString();
        yearIds[qId] = yearId.toString();

        if (!newAttempts[qId]) newAttempts[qId] = 0;
        if (!newTimeTaken[qId]) newTimeTaken[qId] = 0;
      });

      const payload = {
        NumberOfAttempts: newAttempts,
        TimeTaken: newTimeTaken,
        lessonIds,
        yearIds,
      };
      onQuestionsComplete(payload);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  const QuestionComponent = currentQuestion
    ? componentMap[resolveQuestionType(currentQuestion.fields)]
    : null;

  return (
    <div className=" w-full  items-center">
      <div className="flex-1 flex items-center justify-center ">
        <ErrorBoundary>
          {QuestionComponent ? (
            <div className="">
              <QuestionComponent
                key={`question-${currentQuestion.pk}`}
                questionData={currentQuestion.fields}
                onCorrectAnswer={handleAnswer}
                colorTheme={colors.primaryDarkBlue}
                attempts={attempts[currentQuestion.pk.toString()]}
                yearId={yearId}
              />
            </div>
          ) : (
            <div className="text-white text-xl">
              This question type ({currentQuestion.fields?.questionType}) is not
              supported yet.
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default React.memo(HandleAIQuestions);
